import { type FC, useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import Select from "react-select";

import {
  type AaFeatureStats,
  type GenomeBrowserStatistics,
  type InputTableQuerySpec,
  type NuFeatureStats,
  type Organism,
  type TableQuerySpec,
} from "@/graphql.ts";
import { onRestrictionChange } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE, TableRestriction } from "@/settings/settings.ts";

interface ITableRestrictionsDropdownProps {
  activeTableSpec: InputTableQuerySpec;
}

interface IRestrictionMenuItem {
  restriction: TableRestriction;
  isDisabled: boolean; // if not applicable e.g. GENOME restriction when too many rows
}

const GENOME_RESTRICTION_THRESHOLD = 10000;
const TableRestrictionsDropdown: FC<ITableRestrictionsDropdownProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const activeTableRestriction = useSelector<IAppState, TableRestriction>((state) => state.tableData.rowRestriction);
  const genomeStatistics = useSelector<IAppState, GenomeBrowserStatistics>((state) => state.genomeBrowser.statistics);
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome, shallowEqual);

  const [restrictionMenuItems, setRestrictionMenuItems] = useState<IRestrictionMenuItem[]>([]);

  const isMenuOptionDisabled = useCallback(
    (restriction: TableRestriction): boolean => {
      switch (restriction) {
        case TableRestriction.SELECTION:
          return isSelectionRestrictionDisabled(props.activeTableSpec);
        case TableRestriction.GENOME:
          return isGenomeRestrictionDisabled(props.activeTableSpec, genomeStatistics);
        default:
          return false;
      }
    },
    [props.activeTableSpec, genomeStatistics],
  );

  useEffect(() => {
    const menuItems = [
      TableRestriction.GENOME,
      TableRestriction.REGION,
      TableRestriction.CONTIG,
      TableRestriction.SELECTION,
    ].map((tableRestriction: TableRestriction) => {
      return {
        restriction: tableRestriction,
        isDisabled: isMenuOptionDisabled(tableRestriction),
      };
    });
    setRestrictionMenuItems(menuItems);
  }, [genomeStatistics, props.activeTableSpec]);

  // make sure the active row restriction is not disabled, otherwise get back to the default
  useEffect(() => {
    const activeRestriction: IRestrictionMenuItem = restrictionMenuItems.find(
      (restrictionOption: IRestrictionMenuItem) => restrictionOption.restriction === activeTableRestriction,
    );

    if (activeRestriction?.isDisabled) {
      dispatch(onRestrictionChange(TableRestriction.DEFAULT)); // back to the default
    }
  }, [restrictionMenuItems, activeTableRestriction]);

  const onChange = useCallback((restrictionMenuItem: IRestrictionMenuItem) => {
    if (!restrictionMenuItem.isDisabled) {
      dispatch(onRestrictionChange(restrictionMenuItem.restriction));
    }
  }, []);

  const restrictionOptionRenderer = useCallback(
    (restrictionMenuItem: IRestrictionMenuItem) => {
      let label = null;
      switch (restrictionMenuItem.restriction) {
        case TableRestriction.GENOME:
          label = `Entire Genome`;
          break;
        case TableRestriction.CONTIG:
          label = "Current Contig";
          break;
        case TableRestriction.REGION:
          label = "Visible Range";
          break;
        case TableRestriction.SELECTION:
          label = "Selected Items";
          break;
      }
      return <span className={`table-dropdown-option`}>{label}</span>;
    },
    [genome],
  );

  const activeRestriction: IRestrictionMenuItem = restrictionMenuItems.find(
    (restrictionOption: IRestrictionMenuItem) => restrictionOption.restriction === activeTableRestriction,
  );

  return (
    <div style={{ width: "200px" }}>
      <Select
        className={"react-select"}
        classNamePrefix={"react-select"}
        value={activeRestriction}
        isClearable={false}
        isSearchable={false}
        options={restrictionMenuItems}
        formatOptionLabel={restrictionOptionRenderer}
        onChange={onChange}
        getOptionValue={(option: IRestrictionMenuItem) => option.restriction}
        closeMenuOnSelect={true}
        openMenuOnFocus={false}
      />
    </div>
  );
};

const isGenomeRestrictionDisabled = (activeTableSpec: TableQuerySpec, stats: GenomeBrowserStatistics): boolean => {
  if (!stats || !activeTableSpec) {
    return false;
  }

  switch (activeTableSpec.resultType) {
    case ENTITY_TYPE.GENE:
      return stats.geneCount > GENOME_RESTRICTION_THRESHOLD;
    case ENTITY_TYPE.PROTEIN_LOCATION:
      return stats.proteinsCount > GENOME_RESTRICTION_THRESHOLD;
    case ENTITY_TYPE.TRANSCRIPT_LOCATION:
      return stats.transcriptsCount > GENOME_RESTRICTION_THRESHOLD;
    case ENTITY_TYPE.VARIANT:
      return stats.variantsCount > GENOME_RESTRICTION_THRESHOLD;
    case ENTITY_TYPE.VCF_VARIANT:
      return false;
    case ENTITY_TYPE.AAFEATURE: {
      const aaFeatureStatRet: AaFeatureStats = stats.aaFeaturesCount.find(
        (aaFeatureStat: AaFeatureStats) => aaFeatureStat.featureTypeId === activeTableSpec.resultSubTypeId,
      );

      return aaFeatureStatRet && aaFeatureStatRet.count > GENOME_RESTRICTION_THRESHOLD;
    }
    case ENTITY_TYPE.NUFEATURE: {
      const nuFeatureStatRet: NuFeatureStats = stats.nuFeaturesCount.find(
        (nuFeatureStat: NuFeatureStats) => nuFeatureStat.featureTypeId === activeTableSpec.resultSubTypeId,
      );

      return nuFeatureStatRet && nuFeatureStatRet.count > GENOME_RESTRICTION_THRESHOLD;
    }
    default:
      return false;
  }
};

const isSelectionRestrictionDisabled = (activeTableSpec: TableQuerySpec): boolean => {
  return activeTableSpec?.resultType === ENTITY_TYPE.REGION_FLAG; // region flags are not selectable
};

export { TableRestrictionsDropdown };
